import PocketBase, { ClientResponseError } from 'pocketbase'

/**
 * The same self-hosted PocketBase both apps sign into. One account, three surfaces now.
 *
 * The SDK persists the auth token in localStorage on its own, so a reload keeps her signed in
 * without anything here.
 */
export const pb = new PocketBase('https://api.torahly.app')

/**
 * True when the server refused the token itself rather than the request merely failing.
 *
 * This distinction is carried over from the Android client, where getting it wrong is what
 * makes data quietly vanish. A dropped connection must never sign her out, and a dead token
 * must never be kept: keeping one leaves the client believing it is signed in while every list
 * rule beginning `@request.auth.id != ""` matches nothing, so the page renders empty with no
 * error anywhere and no clue why.
 *
 * 404 counts. PocketBase answers an unauthorised read of a record with 404 rather than 403, so
 * as not to confirm the record exists.
 */
export function isRejection(error: unknown): boolean {
  if (!(error instanceof ClientResponseError)) return false
  return error.status === 401 || error.status === 403 || error.status === 404
}

/**
 * True when the SDK cancelled the request rather than the request failing.
 *
 * PocketBase auto-cancels a second in-flight request to the same endpoint and rejects the first
 * with a `ClientResponseError` carrying `isAbort` and status 0. React's StrictMode runs every
 * effect twice in development, so a list that loads once on screen fires twice here, and the
 * cancelled one used to surface as "Could not reach the server. Check your connection." above a
 * page that had in fact loaded perfectly.
 *
 * A cancelled request is not a failure and must never be shown. Every catch that renders an error
 * checks this first.
 */
export function isAbort(error: unknown): boolean {
  if (error instanceof ClientResponseError) return error.isAbort === true
  return error instanceof DOMException && error.name === 'AbortError'
}

/**
 * PocketBase nests validation errors per field, and the field message is nearly always the
 * useful one ("The email is invalid or already in use."). Same extraction the app does.
 */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof ClientResponseError) {
    const data = error.response?.data as Record<string, { message?: string }> | undefined
    if (data) {
      for (const key of Object.keys(data)) {
        const message = data[key]?.message
        if (message) return message
      }
    }
    if (error.status === 0) return 'Could not reach the server. Check your connection.'
    if (error.message) return error.message
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}

/**
 * Like [errorMessage], but with the refused field's name in front of its message, because
 * "Invalid value." on its own does not say which of twenty boxes the server would not take.
 */
export function fieldErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof ClientResponseError) {
    const data = error.response?.data as Record<string, { message?: string }> | undefined
    if (data) {
      for (const key of Object.keys(data)) {
        const message = data[key]?.message
        if (message) return `${key}: ${message}`
      }
    }
  }
  return errorMessage(error, fallback)
}

/**
 * True when the server refused a write on a rule rather than on the data.
 *
 * The three shapes are not obvious and getting them wrong sends you after the wrong fix:
 *
 *   403  refused outright.
 *   404  refused on view/update/delete. PocketBase will not confirm a record exists to someone
 *        who may not read it, so a denied update is indistinguishable from a missing row.
 *   400  refused on CREATE, as `{"data":{},"message":"Failed to create record."}`. This is the
 *        trap. A 400 normally means validation, and `backend/tools/supplements_rule_check.mjs`
 *        printed exactly that advice while the real cause was the create rule. The tell is the
 *        empty `data`: a validation failure always names at least one field in it, and a rule
 *        refusal never names any.
 */
export function isWriteRefusal(error: unknown): boolean {
  if (!(error instanceof ClientResponseError)) return false
  if (error.status === 403 || error.status === 404) return true
  if (error.status !== 400) return false
  const data = error.response?.data as Record<string, unknown> | undefined
  return !data || Object.keys(data).length === 0
}

/**
 * The sentence for a write the server refused, rather than "nothing was changed".
 *
 * It points at the collection's rule and not at the account, and that is the finding rather than
 * a hedge. A reviewer holding `["admin"]` whose role-gated READS all succeed (`forum_reports`
 * lists on the same `@request.auth.role ?= "..."` expression) is being refused by the rule on the
 * collection being written, which means the live rule is not the one in `backend/*.json`. A rule
 * left null in PocketBase means superusers only, and reads keep working through their own rules,
 * so the collection looks half alive.
 *
 * Anything that is not a refusal keeps the server's own words, which for a validation error names
 * the field.
 */
export function writeErrorMessage(error: unknown, fallback: string): string {
  if (isWriteRefusal(error)) {
    return (
      'The server refused the write on this rule, not on the data. Reads are gated on the same role ' +
      'expression and work, so this is the live rule rather than your account: it is not the one ' +
      'in the repo, or it is written with `?=` on the role, which this server does not match ' +
      'against a multi-select role. Apply backend/1789000000_supplements_rule_each.js.'
    )
  }
  return errorMessage(error, fallback)
}
